// Implements: F-IDN-012, F-UIX-009
/**
 * The six sections of `/cuenta` as the v3 mockup draws them (`tabsNav`): a named `nav` of links,
 * the current one marked `aria-current="page"`. They are links and not a `tablist` because each
 * section is its own address (`/cuenta/:tab`), reachable from the user menu and the palette.
 *
 * The user menu and the palette list four of them (`app/layout/accountTabs.ts`); Contraseña and
 * Verificación en dos pasos are reached from here and from the Perfil security row.
 */

import type { ReactElement } from "react";
import { Link } from "react-router";

import type { IconName } from "../../../shared/design-system/icons";
import { useTranslation } from "../../../shared/i18n";
import type { TranslationKey } from "../../../shared/i18n";
import { Icon } from "../../../shared/ui/atoms/Icon";
import styles from "./AccountTabs.module.css";

export const ACCOUNT_BASE = "/cuenta";

export type AccountSection = "perfil" | "contrasena" | "2fa" | "sesiones" | "accesos" | "datos";

interface SectionEntry {
  readonly segment: AccountSection;
  readonly labelKey: Extract<TranslationKey, `account.tabs.${string}`>;
  readonly icon: IconName;
}

export const ACCOUNT_SECTIONS: readonly SectionEntry[] = [
  { segment: "perfil", labelKey: "account.tabs.profile", icon: "user" },
  { segment: "contrasena", labelKey: "account.tabs.password", icon: "lock" },
  { segment: "2fa", labelKey: "account.tabs.totp", icon: "phone" },
  { segment: "sesiones", labelKey: "account.tabs.sessions", icon: "monitor" },
  { segment: "accesos", labelKey: "account.tabs.accessLog", icon: "history" },
  { segment: "datos", labelKey: "account.tabs.data", icon: "download" },
];

/** The section an address segment names, or `undefined` for anything else. */
export function accountSectionOf(segment: string): AccountSection | undefined {
  return ACCOUNT_SECTIONS.find((entry) => entry.segment === segment)?.segment;
}

export function accountSectionHref(segment: AccountSection): string {
  return `${ACCOUNT_BASE}/${segment}`;
}

export interface AccountTabsProps {
  readonly current: AccountSection;
}

export function AccountTabs({ current }: AccountTabsProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={styles.strip}>
      <nav className={styles.tabs} aria-label={t("account.tabs.label")}>
        {ACCOUNT_SECTIONS.map((entry) => (
          <Link
            key={entry.segment}
            className={styles.tab}
            to={accountSectionHref(entry.segment)}
            aria-current={entry.segment === current ? "page" : undefined}
          >
            <Icon name={entry.icon} size="sm" />
            {t(entry.labelKey)}
          </Link>
        ))}
      </nav>
    </div>
  );
}
